import { useState, useEffect } from "react";
import styled from "styled-components";
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { useLanguage } from "@/app/utils/LanguageContext";
import { textSize, text_size } from "@/app/utils/MixinSize";
import { oddType } from "@/app/utils/constant/odd";

const StyledTableHeadCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.root}`]: {
        backgroundColor: "#E1E1E1",
        ...textSize(11),
        fontWeight: 400,
        color: "#333333",
        height: "32px",
        padding: "8px 8px 8px 12px",
        lineHeight: "inherit"
    },
}));

const StyledTableHeadRow = styled(TableHead)(() => ({
    'th: first-child': {
        paddingRight: "8px",
        paddingLeft: "15px",
    }
}));

const StyledTableCell = styled(TableCell)<{ ischinese?: boolean }>(({ ischinese }) => ({
    [`&.${tableCellClasses.root}`]: {
        color: "#333333",
    },
    [`&.${tableCellClasses.body}`]: {
        ...textSize(13),
        fontWeight: 500,
        padding: "8px 12px",
        verticalAlign: "top",
        textAlign: "left",
        minHeight: "33px",
        width: ischinese ? "25%" : "50%",
        lineHeight: "17px"
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    '&.dividendRow2': {
        backgroundColor: "#F4F4F4",
    },
    '&.dividendRow1': {
        backgroundColor: "#FFFFFF",
    },
    'td, th': {
        border: 0,
    },
    'td:last-child, th:last-child': {
        paddingRight: "8px",
    },
    'td: first-child': {
        paddingRight: "8px",
        paddingLeft: "15px",
    }
}));

const StyledTableChildRow = styled('div')(() => ({
    backgroundColor: "inherit",
}));

const Header = styled.div`
    padding: 10px 12px 10px 12px;
    width: 100%;
    height: 40px;
    border-radius: 4px 4px 0px 0px;
    background: #132b68;
    display: flex;
    align-items: center;
    ${text_size(15)}
    font-weight: 400;
    line-height: 16px;
    color: white;
`;

const StyledRemarkDiv = styled('span')(() => ({
    'p': {
        ...textSize(11),
        fontWeight: 500,
        lineHeight: "16px",
        paddingTop: "10px",
        color: "#333333"
    }
}));

const StyledTableContainer = styled(TableContainer)(() => ({
    borderRadius: "0 0 4px 4px"
}));

const StyledTable = styled(Table)(() => ({
    '& thead th:nth-child(1)': { width: '17%' },
    '& thead th:nth-child(2)': { width: '17%' },
    '& thead th:nth-child(3)': { width: '22%', textAlign: "right" },
    '& tbody tr td:nth-child(1)': { width: '17%' },
    '& tbody tr td:nth-child(2)': { width: '17%' },
    '& tbody tr td:last-child': { width: '22%', textAlign: "right" },
}));

const MStyledTable = styled(Table)(() => ({
    backgroundColor: "#E1E1E1",
    '& thead th': { alignContent: "baseline" },
    '& thead th:nth-child(2)': { whiteSpace: "break-spaces" },
    '& thead th:nth-child(3)': { whiteSpace: "nowrap", textAlign: "right" },
    '& tbody tr td:last-child': { textAlign: "right" },
    '& tr td:first-child': { paddingLeft: "12px" },
    '& tr td:last-child': { paddingRight: "12px" }
}));

const DividendTableWrapper = styled('div')(() => ({
    boxShadow: "0px 2px 10px 0px #0000001A"
}));

type propType = {
    type: string;
    isFullWidth?: boolean;
    data?: any;
    raceNo?: number;
    showDivNote?: boolean;
    hasResult?: boolean;
}

interface divData {
    id: number;
    pool: string;
    winCombination: string[];
    dividend: string[];
}

function createData(
    id: number,
    pool: string,
    winCombination: string[],
    dividend: string[]
): divData {
    return {
        id,
        pool,
        winCombination,
        dividend
    }
}


const formatConv = (number:any) => {
    if(Number(number) != number) return '-'
    const numList = parseFloat(number).toFixed(2).toString().split('.')
    numList[0] = Number(numList[0]).toLocaleString()
    return numList.join('.')
  }

const getPMUmsg = (props:any, t:any) => {
    const showPool = [oddType.WIN, oddType.PLA];
    let PMUPool = [] as string[];
    const dividendInfo = props.data.filter((x:any)=> x.leg.races[x.leg.races.length-1]==props.raceNo
        && x.comingleStatus > 0
        && (x.status=="PAYOUT" || x.status.indexOf("REFUND")>=0) && showPool.includes(x.oddsType));

    dividendInfo.map((item:any) => {
        if (!PMUPool.includes(item.oddsType)) { 
            PMUPool.push(item.oddsType);
        }
        
    })
    if(PMUPool.length>1)
    {
        return t('result_france_outbound_PMU').replace('{0}', t('result_france_outbound_PMU_oddsType_WP'));
    }else if(PMUPool.length===1)
    {
        return t('result_france_outbound_PMU').replace('{0}', t('oddsType_'+PMUPool[0]));
    }

    return ''
  }

const GetDivData=(props:any, t:any):divData[]=>{
    if(props.data?.length===0) return [];

    const racePool = "WIN;PLA;QIN;QPL;IWN;CWA;CWB;CWC;FCT;TCE;TRI;FF;QTT;DBL;TBL;DT;TT;SixUP";
    const divBetType = racePool.split(";");
    const showNonWinCmbPool = ["WIN", "IWN", "CWA" , "CWB" , "CWC"];
    const raceNo = props.raceNo;

    const hasMultiInstances = (betType:any) => {
        return ["DBL", "DT"].includes(betType) || props.data.filter((x:any)=> betType==x.oddsType).length>1;
    }
    const dividendInfo = props.data.filter((x:any)=> x.leg.races[x.leg.races.length-1]==raceNo
        && (x.status=="PAYOUT" || x.status.indexOf("REFUND")>=0) && divBetType.includes(x.oddsType));

    let idx = 0;
    const divCreateData : divData[] = []
    divBetType.map((divItem,index) => {

        const pool =  dividendInfo.filter((x:any)=> divItem==x.oddsType)[0];
        if (pool==null) {
            return null;
        }
        const showNonWinCmb = showNonWinCmbPool.includes(divItem);
        const isRefund = pool.status.indexOf("REFUND")>=0;
        const dividends = pool.dividends?.sort((x:any,y:any) => x.seq-y.seq);
        
        const pool_t = `${['DBL', 'TBL', 'DT','TT','SixUP'].includes(divItem) && hasMultiInstances(divItem) ? t(`dividend_pool_no_${pool.leg.number}`) : ''}${t(`oddsType_${divItem}`)}`;
        if(isRefund)
        {
            divCreateData.push(createData(idx, pool_t, ["-"], [t(`dividend_refunded`)]));   
        }else
        {
            const winCombs = [] as string[];
            const divResults = [] as string[];
            dividends.map((item:any) => {
                if (!(!showNonWinCmb && item.div==="NotWin" )) { 
                    const comb = item.winComb.replace(/,/g,', ').replace(/F/g,t('dividend_any_comb'));
                    const winComb = ['TT'].includes(divItem) && item.type === 'Consolation' ? `${t('dividend_consolation')} ${comb}` : comb
                    const partial = item.partial ? `/$${item.partialUnit}`: ''
                    const divResult = ['NOTWIN','UNBACK'].includes(item.div.toUpperCase()) ? t(`dividend_${item.div.toLowerCase()}`) : `${formatConv(item.div)}${partial}`

                    winCombs.push(winComb);
                    divResults.push(divResult);
                }
                
            })

            winCombs.length>0 && divResults.length>0 && divCreateData.push(createData(idx, pool_t, winCombs, divResults));
        }
        idx++;
    })

    return divCreateData

}

const DividendTable: React.FC<propType> = (props: propType) => {
    const { state: lang } = useLanguage();
    const { t } = lang;
    const [isFullWidth, setIsFullWidth] = useState<boolean>(true); // disable in All Result page
    const [showDivNote, setShowDivNote] = useState<boolean>(true);

    useEffect(() => {
        setIsFullWidth(props.isFullWidth!);
        setShowDivNote(props.showDivNote!)
    }, [props]);

    const dividendRecords=GetDivData(props,t);

    const PUMmsg=getPMUmsg(props,t);

    const displayDividend = dividendRecords.length > 0;

    const SimulcastRemark: React.FC = () =>
        <StyledRemarkDiv>
            <p>{t("result_simulcast_remark")}</p>
        </StyledRemarkDiv>
    const SimulcastRemarkNonWagering: React.FC = () =>
        <StyledRemarkDiv>
            <p>{t("result_simulcast_remark_non_wagering")}</p>
        </StyledRemarkDiv>

    if(!displayDividend)
    {return (props.hasResult? <SimulcastRemarkNonWagering />:<></>)}

    return (<>
        {props.type === "pc" ?
            <>
            <DividendTableWrapper id="dividendTableWrapper">
                <Header>{t("dividend")}</Header>
                <StyledTableContainer>
                    <StyledTable>
                        <StyledTableHeadRow>
                            <StyledTableHeadCell>{t("dividend_pool")}</StyledTableHeadCell>
                            <StyledTableHeadCell>{t("dividend_win_combination")}</StyledTableHeadCell>
                            <StyledTableHeadCell>{t("dividend_result")}</StyledTableHeadCell>
                        </StyledTableHeadRow>
                        <TableBody>
                            {dividendRecords.map((row,idx1) => (
                                row.winCombination.map((winC, idx2) =>    
                                    <StyledTableRow key={row.id+ '_' + idx2} className={idx1 % 2 == 0 ? "dividendRow1" : "dividendRow2"}>
                                        {idx2==0 && <StyledTableCell rowSpan={row.winCombination.length}>{row.pool}</StyledTableCell>}
                                        <StyledTableCell style={{ paddingTop: idx2==0 ? "8px" : "0", paddingLeft: "12px" }}>{winC}</StyledTableCell>
                                        <StyledTableCell style={{ paddingTop: idx2==0 ? "8px" : "0", textAlign: "right" }}>{row.dividend?.[idx2]}</StyledTableCell>
                                        {isFullWidth ? <StyledTableCell></StyledTableCell> : null}
                                    </StyledTableRow>
                                )       
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>
            </DividendTableWrapper>
            {showDivNote ? <>
                {/* {PUMmsg !=='' ? <StyledRemarkDiv><p>{PUMmsg}</p></StyledRemarkDiv> : <></>} */}
                <SimulcastRemark />
                {PUMmsg !== '' ? <StyledRemarkDiv>
                    <p id={"DiviDend_PMUNote"} dangerouslySetInnerHTML={{ __html: t('result_france_outbound_PMU_Note') }} />
                </StyledRemarkDiv> : null}
                </> : <></>}
            </>
            :
            <>
            <Grid id={`results_dividend_${props.raceNo}_mobile`} className="mobile">
                <Header><p>{t('dividend')}</p></Header>
                <StyledTableContainer>
                    <MStyledTable>
                        <StyledTableHeadRow>
                            <StyledTableHeadCell>{t("dividend_pool")}</StyledTableHeadCell>
                            <StyledTableHeadCell>{t("dividend_win_combination")}</StyledTableHeadCell>
                            <StyledTableHeadCell>{t("dividend_result")}</StyledTableHeadCell>
                        </StyledTableHeadRow>
                        <TableBody>
                            {dividendRecords.map((row,idx1) => (
                                row.winCombination.map((winC, idx2) =>    
                                    <StyledTableRow key={row.id+ '_' + idx2} className={idx1 % 2 == 0 ? "dividendRow1" : "dividendRow2"}>
                                        {idx2==0 && <StyledTableCell rowSpan={row.winCombination.length}>{row.pool}</StyledTableCell>}
                                        <StyledTableCell style={{ paddingTop: idx2==0 ? "8px" : "0", paddingLeft: "12px" }}>{winC}</StyledTableCell>
                                        <StyledTableCell style={{ paddingTop: idx2==0 ? "8px" : "0" }}>{row.dividend?.[idx2]}</StyledTableCell>
                                    </StyledTableRow>
                                )       
                            ))}
                        </TableBody>
                    </MStyledTable>
                </StyledTableContainer>
            </Grid>
            {showDivNote ? <>
            {/* {PUMmsg !=='' ? <StyledRemarkDiv><p>{PUMmsg}</p></StyledRemarkDiv> : <></>} */}
            <SimulcastRemark />
            {PUMmsg !== '' ? <StyledRemarkDiv>
                    <p id={"DiviDend_PMUNote"} dangerouslySetInnerHTML={{ __html: t('result_france_outbound_PMU_Note') }} />
                </StyledRemarkDiv> : null}
            </> : <></>}
            </>
        }
    </>)
}

export default DividendTable;